import * as S from './styles';
import { IPresenterProps } from './types';
import CustomInput from '../../components/Input/Container';
import COLORS from '../../utils/Colors';
import Navbar from '../../components/NavBar2/Container';
import Footer from '../../components/Footer/Container';
import { Apple, Smartphone, ChevronDown, RefreshCw, User } from 'lucide-react';

// Phone mockup component
const PhoneMockupContent = () => {
    const employees = [
        { initial: 'M', name: 'Miguel', color: '#f97316' },
        { initial: 'S', name: 'Sara', color: '#ec4899' },
        { initial: 'C', name: 'Carlos', color: '#8b5cf6' },
    ];

    return (
        <S.PhoneMockupContainer>
            <S.PhoneMockup>
                <S.PhoneMockupScreen>
                    <S.MockupHeader>
                        <S.MockupDate>
                            Mie 31 Julio <ChevronDown size={16} />
                        </S.MockupDate>
                        <S.MockupIcons>
                            <RefreshCw size={18} />
                            <User size={18} />
                        </S.MockupIcons>
                    </S.MockupHeader>

                    <S.EmployeeRow>
                        {employees.map((emp) => (
                            <S.EmployeeItem key={emp.name}>
                                <S.EmployeeAvatar $bgColor={emp.color}>
                                    {emp.initial}
                                </S.EmployeeAvatar>
                                <S.EmployeeName>{emp.name}</S.EmployeeName>
                            </S.EmployeeItem>
                        ))}
                    </S.EmployeeRow>

                    <S.MockupCalendar>
                        <S.TimeLabel>9:00 - 10:00</S.TimeLabel>
                        <S.CalendarRow>
                            <S.CalendarSlot $isBooked $color="#f97316" />
                            <S.CalendarSlot />
                            <S.CalendarSlot />
                        </S.CalendarRow>
                        <S.TimeLabel>10:00 - 11:00</S.TimeLabel>
                        <S.CalendarRow>
                            <S.CalendarSlot />
                            <S.CalendarSlot $isBooked $color="#ec4899" />
                            <S.CalendarSlot $isBooked $color="#8b5cf6" />
                        </S.CalendarRow>
                        <S.TimeLabel>11:00 - 12:00</S.TimeLabel>
                        <S.CalendarRow>
                            <S.CalendarSlot $isBooked $color="#f97316" />
                            <S.CalendarSlot $isBooked $color="#ec4899" />
                            <S.CalendarSlot />
                        </S.CalendarRow>
                    </S.MockupCalendar>
                </S.PhoneMockupScreen>
            </S.PhoneMockup>
        </S.PhoneMockupContainer>
    );
};

const BusinessRegistrationPresenter = (props: IPresenterProps) => {
    const APP_STORE_URL = 'https://apps.apple.com/uy/app/mora-biz/id6476923418';
    const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.marcosberrutti.mora';

    return (
        <S.PageWrapper>
            <Navbar isSearchBarHidden={true} />

            {/* Hero Section */}
            <S.HeroSection>
                <S.HeroTitle>
                    Gestiona tu negocio desde cualquier lugar
                </S.HeroTitle>
                <S.HeroSubtitle>
                    Con Mora Biz, administra citas, empleados y clientes desde tu celular.
                    La forma mas simple de hacer crecer tu negocio.
                </S.HeroSubtitle>
            </S.HeroSection>

            {/* App Showcase Section */}
            <S.AppShowcaseSection>
                <S.AppCard>
                    <S.AppCardHeader>
                        Disponible en <Apple size={16} /> <Smartphone size={16} />
                    </S.AppCardHeader>

                    <S.AppCardTitle>
                        App para profesionales
                    </S.AppCardTitle>

                    <S.AppCardSubtitle>
                        Comienza con Mora Biz y nunca pierdas la oportunidad
                        de conectar con clientes y hacer crecer tu negocio.
                    </S.AppCardSubtitle>

                    <S.StoreBadges>
                        <S.StoreBadge href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                            <Apple size={20} />
                            <S.StoreBadgeText>
                                <S.StoreBadgeSmall>Descargar en</S.StoreBadgeSmall>
                                <S.StoreBadgeLarge>App Store</S.StoreBadgeLarge>
                            </S.StoreBadgeText>
                        </S.StoreBadge>

                        <S.StoreBadge href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                            <Smartphone size={20} />
                            <S.StoreBadgeText>
                                <S.StoreBadgeSmall>Disponible en</S.StoreBadgeSmall>
                                <S.StoreBadgeLarge>Google Play</S.StoreBadgeLarge>
                            </S.StoreBadgeText>
                        </S.StoreBadge>
                    </S.StoreBadges>

                    <PhoneMockupContent />
                </S.AppCard>
            </S.AppShowcaseSection>

            {/* Registration Form Section */}
            <S.FormSection>
                <S.FormCard>
                    <S.FormTitle>
                        Interesado? Dejanos tus datos
                    </S.FormTitle>

                    <S.FormGrid>
                        <S.FormField>
                            <S.Label htmlFor="name">Nombre Completo</S.Label>
                            <CustomInput
                                inputType={'text'}
                                value={props.formData.name}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.onChange(e, 'name')}
                                bgColor={COLORS.white}
                                placeHolder='Tu nombre'
                                disabled={false}
                                showDeleteTextIcon={false}
                                deleteText={() => { }}
                                textColor={COLORS.black}
                                showUnMaskIcon={false}
                                changeInputType={false}
                                showError={false}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label htmlFor="email">Email</S.Label>
                            <CustomInput
                                inputType={'text'}
                                value={props.formData.email}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.onChange(e, 'email')}
                                bgColor={COLORS.white}
                                placeHolder='tu@email.com'
                                disabled={false}
                                showDeleteTextIcon={false}
                                deleteText={() => { }}
                                textColor={COLORS.black}
                                showUnMaskIcon={false}
                                changeInputType={false}
                                showError={false}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label htmlFor="city">Ciudad</S.Label>
                            <CustomInput
                                inputType={'text'}
                                value={props.formData.city}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.onChange(e, 'city')}
                                bgColor={COLORS.white}
                                placeHolder='Tu ciudad'
                                disabled={false}
                                showDeleteTextIcon={false}
                                deleteText={() => { }}
                                textColor={COLORS.black}
                                showUnMaskIcon={false}
                                changeInputType={false}
                                showError={false}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label htmlFor="phone">Telefono</S.Label>
                            <CustomInput
                                inputType={'text'}
                                value={props.formData.phone}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.onChange(e, 'phone')}
                                bgColor={COLORS.white}
                                placeHolder='+54 11 1234 5678'
                                disabled={false}
                                showDeleteTextIcon={false}
                                deleteText={() => { }}
                                textColor={COLORS.black}
                                showUnMaskIcon={false}
                                changeInputType={false}
                                showError={false}
                            />
                        </S.FormField>

                        <S.FormField className="full-width">
                            <S.Label htmlFor="businessName">Nombre del Negocio</S.Label>
                            <CustomInput
                                inputType={'text'}
                                value={props.formData.businessName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.onChange(e, 'businessName')}
                                bgColor={COLORS.white}
                                placeHolder='Nombre de tu negocio'
                                disabled={false}
                                showDeleteTextIcon={false}
                                deleteText={() => { }}
                                textColor={COLORS.black}
                                showUnMaskIcon={false}
                                changeInputType={false}
                                showError={false}
                            />
                        </S.FormField>
                    </S.FormGrid>

                    <S.SubmitButton type="button">
                        Enviar
                    </S.SubmitButton>
                </S.FormCard>
            </S.FormSection>

            <Footer />
        </S.PageWrapper>
    );
};

export default BusinessRegistrationPresenter;
